"use client"

import { useEffect, useState } from "react"
import { ArrowUp, Home } from "lucide-react"
import "../assets/css/PottingMixLearnMore.css"

export default function PottingMixLearnMore() {
  const [showBackToTop, setShowBackToTop] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      setShowBackToTop(scrollTop > 300)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    })
  }

  const goToHome = () => {
    window.location.href = "/"
  }

  return (
    <div className="learn-more-container potting-mix-learn-more">
      <h1>Premium Potting Mix</h1>
      <h2 className="tagline">Your All-in-One Organic Growing Solution</h2>
      
      <p className="intro-text">
        Finding the right soil and fertilizer for healthy plant growth can be a challenge for households and businesses. 
        Our Premium Potting Mix is the perfect solution—an international standard blend designed for convenience and superior results. 
        This mix does not include ground soil, ensuring a clean, organic medium that is ready to use. Made from natural components 
        like coconut husks, peat, and rice husks extracted from Sri Lanka, it is germinated to prevent harmful bacteria and diseases. 
        Engineered to retain moisture, protect against temperature extremes, and provide balanced nutrients, this mix is ideal for 
        germinating seeds and growing vegetables, flowers, and herbs—indoors or outdoors. A hassle-free way to achieve vibrant, 
        healthy plants without soil searches or separate fertilizers.
      </p>

      <section className="product-label-section">
        <h2>Product Information</h2>
        
        <div className="label-container">
          <div className="label-section front-label">
            <h3>Front of Package</h3>
            <div className="label-content">
              <div className="brand-name">Green Beat Ceylon Pvt Ltd</div>
              <div className="product-name">Premium Potting Mix</div>
              <div className="product-badges">
                <span className="badge">Organic</span>
                <span className="badge">Water-Retaining</span>
                <span className="badge">Suitable for Indoor & Outdoor Plants</span>
              </div>
              <div className="weight-info">Weight: 2.5 kg</div>
              <div className="tagline-label">Perfect for healthy plant growth</div>
            </div>
          </div>

          <div className="label-section back-label">
            <h3>Key Features</h3>
            <div className="label-content">
              <ul className="features-list">
                <li>100% Organic</li>
                <li>Protects against temperature extremes and moisture loss</li>
                <li>Increases soil acidity naturally</li>
                <li>No added fertilizer required—Potting mix feeds the plant up to 6 months</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="usage-section">
        <h2>Usage Instructions</h2>
        
        <div className="usage-grid">
          <div className="usage-card">
            <h3>Best For</h3>
            <p>
              Ideal for germinating vegetable, flower, and herb seeds (e.g., rose, chili) and for nurseries producing crops. 
              Can fill up to 450 seedling tray cells or a medium-size flower pot.
            </p>
          </div>

          <div className="usage-card">
            <h3>How to Use</h3>
            <ol className="usage-steps">
              <li><strong>Position:</strong> Place in a warm position with good light; avoid direct sun and draughts</li>
              <li><strong>Watering:</strong> Keep moist but not wet, ensuring water can drain freely</li>
              <li><strong>Feeding:</strong> This mix contains a fertilizer blend for healthy growth</li>
            </ol>
          </div>
        </div>
      </section>

      <section className="care-section">
        <h2>General Care Guidelines</h2>
        
        <div className="care-grid">
          <div className="care-card">
            <h4>Position</h4>
            <p>Warm, well-lit area with indirect sunlight. Avoid direct sun exposure and strong draughts.</p>
          </div>

          <div className="care-card">
            <h4>Watering</h4>
            <p>Keep soil moist but not soggy. Ensure proper drainage to prevent root rot and waterlogging.</p>
          </div>

          <div className="care-card">
            <h4>Feeding</h4>
            <p>Balanced nutrients are included in the potting mix. No additional fertilizer needed for up to 6 months.</p>
          </div>

          <div className="care-card">
            <h4>Storage</h4>
            <p>Store in a cool, dry place. Keep the bag sealed when not in use to maintain freshness.</p>
          </div>
        </div>
      </section>

      <section className="benefits-section">
        <h2>Why Choose Our Premium Potting Mix?</h2>
        <div className="benefits-list">
          <div className="benefit-item">
            <h4>International Standard Quality</h4>
            <p>Meets globally recognized standards for potting soil composition and quality.</p>
          </div>
          <div className="benefit-item">
            <h4>Organic Origin from Sri Lanka</h4>
            <p>Made from natural components including coconut husks, peat, and rice husks sourced locally from Sri Lanka.</p>
          </div>
          <div className="benefit-item">
            <h4>Ready-to-Use Convenience</h4>
            <p>No mixing or preparation needed—use directly from the bag for immediate planting.</p>
          </div>
          <div className="benefit-item">
            <h4>Disease-Free Germination</h4>
            <p>Specially germinated to prevent harmful bacteria and diseases, ensuring healthy plant growth.</p>
          </div>
          <div className="benefit-item">
            <h4>Superior Moisture & Nutrient Balance</h4>
            <p>Engineered to retain moisture while protecting against temperature extremes and providing balanced nutrition.</p>
          </div>
        </div>
      </section>

      {/* Navigation Arrows */}
      <div className="navigation-arrows">
        <button
          className={`nav-arrow back-to-top ${showBackToTop ? "visible" : ""}`}
          onClick={scrollToTop}
          data-tooltip="Back to Top"
          aria-label="Back to top"
        >
          <ArrowUp />
        </button>
        <button
          className="nav-arrow back-to-home"
          onClick={goToHome}
          data-tooltip="Back to Home"
          aria-label="Back to home"
        >
          <Home />
        </button>
      </div>
    </div>
  )
}
